import { type FC, useEffect, useState, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from 'urql';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl } from 'react-intl';
import { GetProductSkusDocument } from '@packages/gql/generated/shopping/GetProductSkusDocument';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer.client';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';

interface DirectOrderValues {
  articleNumber: string;
}

const messages = defineMessages({
  directOrderHeadline: {
    id: 'DirectOrder.headline',
    defaultMessage: 'Artikelnummer direkt suchen',
  },
  articleNumberLabel: {
    id: 'DirectOrder.articleNumberLabel',
    defaultMessage: 'Artikelnummer',
  },
  articleNumberPlaceholder: {
    id: 'DirectOrder.articleNumberPlaceholder',
    defaultMessage: 'Artikelnummer deines Artikels',
  },
  description: {
    id: 'DirectOrder.description',
    defaultMessage:
      'Schnell und einfach – suche deinen Artikel anhand der Artikelnummer und füge ihn hinzu',
  },
  articleNumberError: {
    id: 'DirectOrder.articleNumber.Error',
    defaultMessage:
      'Wir haben den Artikel leider nicht gefunden, überprüfe deine Eingabe oder nutze die Suchfunktion dafür.',
  },
  search: {
    id: 'DirectOrder.search.ariaLabel',
    defaultMessage: 'Suchen',
  },
});

/* GraphQL */ `
  query GetProductSkus($productId: String!, $locale: String!) {
    product(id: $productId, fallbackId: "", locale: $locale) {
      akl
      sku
    }
  }
`;

const schema = z.object({
  articleNumber: z.string().trim().min(1),
});

export const DirectOrder: FC = () => {
  const { formatMessage, locale } = useIntl();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const lastResolvedIdRef = useRef<string | null>(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
  } = useForm<DirectOrderValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  const productId = watch('articleNumber');
  const [{ data, fetching, error }, executeQuery] = useQuery({
    query: GetProductSkusDocument,
    variables: {
      productId,
      locale,
    },
    pause: true,
  });

  const showError: boolean =
    (!fetching && data && data.product === null) ||
    error !== undefined ||
    errors.articleNumber !== undefined;

  const onSubmit = () => {
    if (lastResolvedIdRef.current === productId && data?.product?.sku) {
      setDialogOpen(true);
      return;
    }
    executeQuery({ requestPolicy: 'cache-first' });
  };

  useEffect(() => {
    if (data?.product?.sku) {
      lastResolvedIdRef.current = productId;
      setDialogOpen(true);
    }
  }, [data, productId]);

  return (
    <div className="w-full bg-white p-1.5 md:py-1.5">
      {data?.product && (
        <MiniDVLayer
          sku={data.product.sku}
          akl={data.product.akl}
          open={dialogOpen}
          quantity={1}
          onClose={() => setDialogOpen(false)}
        />
      )}

      <p className="text-color-text-dark text-size-body-xs my-1.5 font-semibold">
        {formatMessage(messages.directOrderHeadline)}
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className="md:min-h-[6.813rem]">
          <CardContent className="p-2">
            <div className="grid grid-cols-1 grid-rows-[auto_auto] gap-2 md:grid-cols-2 md:grid-rows-1">
              <div className="flex gap-2">
                <div className="w-full">
                  <Input
                    {...register('articleNumber')}
                    label={formatMessage(messages.articleNumberLabel)}
                    placeholder={formatMessage(messages.articleNumberPlaceholder)}
                    helperText={showError ? formatMessage(messages.articleNumberError) : undefined}
                    error={showError}
                    className="w-full max-w-none grow"
                  />
                </div>
                <div className="mt-3">
                  <Button
                    type="submit"
                    className="mr-0 h-4.5 w-4.5 p-0"
                    aria-label={formatMessage(messages.search)}
                    data-testid="direct-order-button"
                  >
                    <div className="icon-emp-search size-4" />
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-size-body-sm mt-1 md:mt-3">
                  {formatMessage(messages.description)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};
